import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LoaderBar, PageHeader, FlexRowBetween } from 'components'
import { AssetList } from 'page-components/asset'
import { AppButton, AppPrimaryButton, AppSecondaryButton } from 'elements'
import { DesktopMode, MobileMode } from 'layout'
import {
    useAsset,
    useAssetCategory,
    useAssetLocation,
    useAssetActivity,
} from 'hooks/asset-manager'
import { useAuth } from 'context/user'
import { Modal, Form } from 'semantic-ui-react'
import { X, QrCode, Printer } from 'phosphor-react'
import {
    buildAssetQrPayload,
    getAssetQrImageUrl,
    printAssetLabel,
} from 'utility/asset-qr'
import { getAssetPermissions } from 'utility/asset-permissions'

export const AssetListScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { user } = useAuth()
    const permissions = getAssetPermissions(user)
    const { captureActivity } = useAssetActivity()
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: 25,
        SearchText: '',
        Location: '',
        Category: '',
        Status: '',
    })

    const {
        assets,
        totalCount,
        loading,
        fetchAssets,
        saveAsset,
        deleteAsset: deleteAssetAPI,
    } = useAsset()

    const { categories, fetchCategories } = useAssetCategory()
    const { locations, fetchLocations } = useAssetLocation()
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openQrModal, setOpenQrModal] = useState(false)
    const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false)
    const [isSavingAsset, setIsSavingAsset] = useState(false)
    const [selectedAssetForQr, setSelectedAssetForQr] = useState(null)
    const [selectedAssetForDelete, setSelectedAssetForDelete] = useState(null)
    const [editingAssetId, setEditingAssetId] = useState(0)
    const [assetForm, setAssetForm] = useState({
        AssetName: '',
        AssetTag: '',
        SerialNumber: '',
        AssetCategoryId: '',
        AssetLocationId: '',
        Description: '',
        Manufacturer: '',
        ModelNumber: '',
        PurchaseDate: '',
        WarrantyEndDate: '',
        Status: '',
        IsActive: true,
    })
    const categoriesForUi = Array.isArray(categories) ? categories : []
    const locationsForUi = Array.isArray(locations) ? locations : []
    const assetsForUi = Array.isArray(assets) ? assets : []

    // Fetch categories and locations on mount
    useEffect(() => {
        if (user?.CompanyId) {
            fetchCategories()
            fetchLocations()
        }
    }, [user?.CompanyId, fetchCategories, fetchLocations])

    const resetAssetForm = () => {
        setEditingAssetId(0)
        setAssetForm({
            AssetName: '',
            AssetTag: '',
            SerialNumber: '',
            AssetCategoryId: '',
            AssetLocationId: '',
            Description: '',
            Manufacturer: '',
            ModelNumber: '',
            PurchaseDate: '',
            WarrantyEndDate: '',
            Status: '',
            IsActive: true,
        })
    }

    // Fetch assets when filter changes
    useEffect(() => {
        if (user?.CompanyId) {
            fetchAssets({
                PageNumber: filter.PageNumber,
                PageSize: filter.PageSize,
                OnlyActive: filter.Status !== 'inactive',
            })
        }
    }, [user?.CompanyId, filter.PageNumber, filter.PageSize, filter.Status, fetchAssets])
    const handleAddAsset = () => {
        if (!permissions.canCreateAsset) return
        resetAssetForm()
        setOpenAddModal(true)
    }

    const handleAddAssetSubmit = async () => {
        if (!permissions.canCreateAsset) return
        if (!assetForm.AssetName?.trim()) return
        setIsSavingAsset(true)
        try {
            const payload = {
                AssetId: editingAssetId || 0,
                AssetName: assetForm.AssetName.trim(),
                AssetTag: assetForm.AssetTag?.trim() || null,
                SerialNumber: assetForm.SerialNumber?.trim() || null,
                AssetCategoryId: Number(assetForm.AssetCategoryId) || 0,
                AssetLocationId: Number(assetForm.AssetLocationId) || 0,
                Description: assetForm.Description?.trim() || null,
                Manufacturer: assetForm.Manufacturer?.trim() || null,
                ModelNumber: assetForm.ModelNumber?.trim() || null,
                PurchaseDate: assetForm.PurchaseDate || null,
                WarrantyEndDate: assetForm.WarrantyEndDate || null,
                Status: assetForm.Status?.trim() || null,
                IsActive: !!assetForm.IsActive,
            }
            const result = await saveAsset(payload)

            const isSaveSuccess =
                result !== null && result !== undefined && result !== false
            if (isSaveSuccess) {
                setOpenAddModal(false)
                resetAssetForm()
                captureActivity({
                    assetId:
                        (typeof result === 'object' ? result?.AssetId : result) ||
                        payload.AssetId ||
                        payload.AssetTag ||
                        '',
                    action: editingAssetId ? 'Asset updated' : 'Asset created',
                })
                await fetchAssets({
                    PageNumber: filter.PageNumber,
                    PageSize: filter.PageSize,
                    OnlyActive: filter.Status !== 'inactive',
                })
            }
        } finally {
            setIsSavingAsset(false)
        }
    }

    const handleViewAsset = (asset) => {
        // Prefer numeric id for API/detail fetch; include full row for demo fallback.
        const assetRouteId =
            asset.AssetId?.toString() || asset.assetId || asset.AssetTag || ''
        captureActivity({
            assetId: asset.AssetId || asset.assetId || '',
                    action: 'Asset detail viewed',
                    assetName: asset.AssetName || '',
                    assetTag: asset.AssetTag || '',
        })
        navigate(`/app/asset-management/${assetRouteId}`, { state: { asset } })
    }

    const handleEditAsset = (asset) => {
        if (!permissions.canEditAsset) return
        setEditingAssetId(Number(asset.AssetId) || 0)
        setAssetForm({
            AssetName: asset.AssetName || '',
            AssetTag: asset.AssetTag || '',
            SerialNumber: asset.SerialNumber || '',
            AssetCategoryId: asset.AssetCategoryId || '',
            AssetLocationId: asset.AssetLocationId || '',
            Description: asset.Description || '',
            Manufacturer: asset.Manufacturer || '',
            ModelNumber: asset.ModelNumber || '',
            PurchaseDate: asset.PurchaseDate
                ? String(asset.PurchaseDate).split('T')[0]
                : '',
            WarrantyEndDate: asset.WarrantyEndDate
                ? String(asset.WarrantyEndDate).split('T')[0]
                : '',
            Status: asset.Status || '',
            IsActive: asset.IsActive !== false,
        })
        setOpenAddModal(true)
    }

    const handleDeleteAsset = async (asset) => {
        if (!permissions.canDeleteAsset) return
        setSelectedAssetForDelete(asset)
        setOpenDeleteConfirmModal(true)
    }

    const confirmDeleteAsset = async () => {
        const asset = selectedAssetForDelete
        if (!asset) return
        const success = await deleteAssetAPI(asset.AssetId || asset.assetId)
        if (success) {
            captureActivity({
                assetId: asset.AssetId || asset.assetId || '',
                action: 'Asset deleted',
                assetName: asset.AssetName || '',
                assetTag: asset.AssetTag || '',
            })
            setOpenDeleteConfirmModal(false)
            setSelectedAssetForDelete(null)
        }
    }

    const handleAssignInspection = (asset) => {
        if (!permissions.canAssignInspection) return
        captureActivity({
            assetId: asset.AssetId || asset.assetId || '',
            action: 'Assign inspection opened',
            assetName: asset.AssetName || '',
            assetTag: asset.AssetTag || '',
        })
        // Navigate to schedule inspection page
        navigate('/app/asset-management/schedule-inspection', { state: { asset } })
    }

    const handleGenerateQR = (asset) => {
        if (!permissions.canGenerateQr) return
        captureActivity({
            assetId: asset.AssetId || asset.assetId || '',
            action: 'QR generated',
            assetName: asset.AssetName || '',
            assetTag: asset.AssetTag || '',
        })
        setSelectedAssetForQr(asset || null)
        setOpenQrModal(true)
    }

    const handlePrintLabel = asset => {
        if (!permissions.canPrintLabel) return
        captureActivity({
            assetId: asset.AssetId || asset.assetId || '',
            action: 'Label printed',
            assetName: asset.AssetName || '',
            assetTag: asset.AssetTag || '',
        })
        const payload = buildAssetQrPayload(asset)
        const qrUrl = getAssetQrImageUrl(payload)
        printAssetLabel({ asset, qrUrl })
    }

    const handleExportAsset = (asset) => {
        // Export asset logic
        console.log('Export clicked', asset)
    }

    const pageChanged = (newPage) => {
        if (typeof newPage === 'number') {
            setFilter(prev => ({
                ...prev,
                PageNumber: newPage,
            }))
        } else {
            setFilter(newPage)
        }
    }

    // Transform API data to match component expectations
    const transformedData = assetsForUi.map(asset => ({
        assetId: asset.AssetTag || asset.AssetId?.toString() || '',
        assetName: asset.AssetName || '',
        category:
            categoriesForUi.find(
                cat => cat.AssetCategoryId === asset.AssetCategoryId,
            )?.CategoryName || '',
        location:
            locationsForUi.find(
                loc => loc.AssetLocationId === asset.AssetLocationId,
            )?.LocationName || '',
        status: asset.IsActive ? 'Active' : 'Inactive',
        ...asset, // Keep original data for detail view
    }))

    // Filter data based on search and filters (client-side filtering for now)
    const filteredData = transformedData.filter(item => {
        if (filter.SearchText && !item.assetName.toLowerCase().includes(filter.SearchText.toLowerCase()) &&
            !item.assetId.toLowerCase().includes(filter.SearchText.toLowerCase())) {
            return false
        }
        if (filter.Location && item.location !== filter.Location) {
            return false
        }
        if (filter.Category && item.category !== filter.Category) {
            return false
        }
        if (filter.Status && item.status.toLowerCase() !== filter.Status.toLowerCase()) {
            return false
        }
        return true
    })

    useEffect(() => {
        const stateAsset = location.state?.editAsset
        if (!stateAsset || !permissions.canEditAsset) return
        handleEditAsset(stateAsset)
    }, [location.state, permissions.canEditAsset])

    return (
        <>
            {/* Desktop Layout */}
            <DesktopMode>
                <FlexRowBetween className="mb-3">
                    <PageHeader header="Assets" />
                    <span>
                        <AppSecondaryButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/types')}
                        >
                            Asset Types
                        </AppSecondaryButton>
                        <AppSecondaryButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/locations')}
                        >
                            Asset Locations
                        </AppSecondaryButton>
                        <AppSecondaryButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/custom-field-manager')}
                        >
                            Custom Fields
                        </AppSecondaryButton>
                        <AppSecondaryButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/audit-log')}
                        >
                            Audit Log
                        </AppSecondaryButton>
                        <AppSecondaryButton
                            type="button"
                            onClick={() =>
                                navigate('/app/asset-management/template-linking')
                            }
                            disabled={!permissions.canViewAssetModule}
                        >
                            Template linking
                        </AppSecondaryButton>
                        <AppSecondaryButton
                            type="button"
                            onClick={() =>
                                navigate('/app/asset-management/schedule-inspections')
                            }
                            disabled={!permissions.canViewAssetModule}
                        >
                            Schedule inspections
                        </AppSecondaryButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={handleAddAsset}
                            disabled={!permissions.canCreateAsset}
                        >
                            + Add Asset
                        </AppPrimaryButton>
                        <AppSecondaryButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/scan')}
                            disabled={!permissions.canUseScanner}
                        >
                            Scan QR
                        </AppSecondaryButton>
                    </span>
                </FlexRowBetween>
            </DesktopMode>

            {/* Mobile Layout */}
            <MobileMode>
                <div className="row mb-3">
                    <div className="col-12">
                        <PageHeader header="Assets" />
                    </div>
                    <div className="col-12 mt-2">
                        <AppButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/types')}
                            fluid
                            style={{ marginBottom: '8px' }}
                        >
                            Asset Types
                        </AppButton>
                        <AppButton
                            type="button"
                            onClick={() =>
                                navigate('/app/asset-management/locations')
                            }
                            fluid
                            style={{ marginBottom: '8px' }}
                        >
                            Asset Locations
                        </AppButton>
                        <AppButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/custom-field-manager')}
                            fluid
                            style={{ marginBottom: '8px' }}
                        >
                            Custom Fields
                        </AppButton>
                        <AppButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/audit-log')}
                            fluid
                            style={{ marginBottom: '8px' }}
                        >
                            Audit Log
                        </AppButton>
                        <AppButton
                            type="button"
                            onClick={() =>
                                navigate('/app/asset-management/template-linking')
                            }
                            fluid
                            style={{ marginBottom: '8px' }}
                            disabled={!permissions.canViewAssetModule}
                        >
                            Template linking
                        </AppButton>
                        <AppButton
                            type="button"
                            onClick={() =>
                                navigate('/app/asset-management/schedule-inspections')
                            }
                            fluid
                            style={{ marginBottom: '8px' }}
                            disabled={!permissions.canViewAssetModule}
                        >
                            Schedule inspections
                        </AppButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={handleAddAsset}
                            fluid
                            style={{ marginBottom: '8px' }}
                            disabled={!permissions.canCreateAsset}
                        >
                            + Add Asset
                        </AppPrimaryButton>
                        <AppButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/scan')}
                            fluid
                            disabled={!permissions.canUseScanner}
                        >
                            Scan QR
                        </AppButton>
                    </div>
                </div>
            </MobileMode>

            <div className="col-12 mb-3">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                            Search
                        </label>
                        <AppButton
                            type="button"
                            onClick={() => navigate('/app/asset-management/search')}
                            style={{ width: '100%', maxWidth: '300px' }}
                        >
                            Search by name or ID
                        </AppButton>
                    </div>
                </div>
            </div>

            <div className="my-3">
                {loading && <LoaderBar content="Loading" />}
                <AssetList
                    data={filteredData}
                    pageChanged={pageChanged}
                    totalCount={filteredData.length || totalCount}
                    filter={filter}
                    onView={handleViewAsset}
                    onEdit={handleEditAsset}
                    onDelete={handleDeleteAsset}
                    onAssignInspection={handleAssignInspection}
                    onGenerateQR={handleGenerateQR}
                    onPrintLabel={handlePrintLabel}
                    onExport={handleExportAsset}
                    permissions={permissions}
                />
            </div>
            <Modal
                open={openAddModal}
                onClose={() => setOpenAddModal(false)}
                size="small"
            >
                <Modal.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <span>{editingAssetId ? 'Edit Asset' : 'Add Asset'}</span>
                        <X
                            className="pointer"
                            size={24}
                            onClick={() => setOpenAddModal(false)}
                        />
                    </div>
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Input
                            label="Asset Name"
                            placeholder="Enter asset name"
                            value={assetForm.AssetName}
                            onChange={(_, { value }) =>
                                setAssetForm(prev => ({
                                    ...prev,
                                    AssetName: value,
                                }))
                            }
                            required
                        />
                        <Form.Input
                            label="Asset ID / Tag"
                            placeholder="Enter asset ID"
                            value={assetForm.AssetTag}
                            onChange={(_, { value }) =>
                                setAssetForm(prev => ({
                                    ...prev,
                                    AssetTag: value,
                                }))
                            }
                        />
                        <Form.Input
                            label="Serial Number"
                            placeholder="Enter serial number"
                            value={assetForm.SerialNumber}
                            onChange={(_, { value }) =>
                                setAssetForm(prev => ({
                                    ...prev,
                                    SerialNumber: value,
                                }))
                            }
                        />
                        <Form.Group widths="equal">
                            <Form.Select
                                label="Category"
                                placeholder="Select category"
                                options={categoriesForUi.map(item => ({
                                    key: item.AssetCategoryId,
                                    text: item.CategoryName,
                                    value: item.AssetCategoryId,
                                }))}
                                value={assetForm.AssetCategoryId}
                                onChange={(_, { value }) =>
                                    setAssetForm(prev => ({
                                        ...prev,
                                        AssetCategoryId: value,
                                    }))
                                }
                            />
                            <Form.Select
                                label="Location"
                                placeholder="Select location"
                                options={locationsForUi.map(item => ({
                                    key: item.AssetLocationId,
                                    text: item.LocationName,
                                    value: item.AssetLocationId,
                                }))}
                                value={assetForm.AssetLocationId}
                                onChange={(_, { value }) =>
                                    setAssetForm(prev => ({
                                        ...prev,
                                        AssetLocationId: value,
                                    }))
                                }
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <Form.Input
                                label="Manufacturer"
                                placeholder="Enter manufacturer"
                                value={assetForm.Manufacturer}
                                onChange={(_, { value }) =>
                                    setAssetForm(prev => ({
                                        ...prev,
                                        Manufacturer: value,
                                    }))
                                }
                            />
                            <Form.Input
                                label="Model Number"
                                placeholder="Enter model number"
                                value={assetForm.ModelNumber}
                                onChange={(_, { value }) =>
                                    setAssetForm(prev => ({
                                        ...prev,
                                        ModelNumber: value,
                                    }))
                                }
                            />
                        </Form.Group>
                        <Form.Input
                            label="Description"
                            placeholder="Enter description"
                            value={assetForm.Description}
                            onChange={(_, { value }) =>
                                setAssetForm(prev => ({
                                    ...prev,
                                    Description: value,
                                }))
                            }
                        />
                        <Form.Group widths="equal">
                            <Form.Input
                                type="date"
                                label="Purchase Date"
                                value={assetForm.PurchaseDate}
                                onChange={(_, { value }) =>
                                    setAssetForm(prev => ({
                                        ...prev,
                                        PurchaseDate: value,
                                    }))
                                }
                            />
                            <Form.Input
                                type="date"
                                label="Warranty End Date"
                                value={assetForm.WarrantyEndDate}
                                onChange={(_, { value }) =>
                                    setAssetForm(prev => ({
                                        ...prev,
                                        WarrantyEndDate: value,
                                    }))
                                }
                            />
                        </Form.Group>
                        <Form.Input
                            label="Status"
                            placeholder="Enter status"
                            value={assetForm.Status}
                            onChange={(_, { value }) =>
                                setAssetForm(prev => ({
                                    ...prev,
                                    Status: value,
                                }))
                            }
                        />
                        <Form.Checkbox
                            label="Active"
                            checked={assetForm.IsActive}
                            onChange={(_, { checked }) =>
                                setAssetForm(prev => ({
                                    ...prev,
                                    IsActive: !!checked,
                                }))
                            }
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setOpenAddModal(false)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton
                        type="button"
                        onClick={handleAddAssetSubmit}
                        disabled={!assetForm.AssetName?.trim()}
                        loading={isSavingAsset}
                    >
                        {editingAssetId ? 'Update' : 'Save'}
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
            <Modal
                open={openDeleteConfirmModal}
                onClose={() => {
                    setOpenDeleteConfirmModal(false)
                    setSelectedAssetForDelete(null)
                }}
                size="tiny"
            >
                <Modal.Header>Delete Asset</Modal.Header>
                <Modal.Content>
                    {`Are you sure you want to delete ${
                        selectedAssetForDelete?.AssetName ||
                        selectedAssetForDelete?.assetName ||
                        'this asset'
                    }?`}
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => {
                            setOpenDeleteConfirmModal(false)
                            setSelectedAssetForDelete(null)
                        }}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton type="button" onClick={confirmDeleteAsset}>
                        Delete
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
            <Modal
                open={openQrModal}
                onClose={() => setOpenQrModal(false)}
                size="tiny"
            >
                <Modal.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <span>Asset QR Code</span>
                        <X
                            className="pointer"
                            size={24}
                            onClick={() => setOpenQrModal(false)}
                        />
                    </div>
                </Modal.Header>
                <Modal.Content>
                    {selectedAssetForQr ? (
                        <div className="d-flex flex-column align-items-center">
                            <strong className="mb-1">
                                {selectedAssetForQr.AssetName ||
                                    selectedAssetForQr.assetName}
                            </strong>
                            <span className="mb-3" style={{ color: '#666' }}>
                                ID:{' '}
                                {selectedAssetForQr.AssetTag ||
                                    selectedAssetForQr.assetId ||
                                    selectedAssetForQr.AssetId}
                            </span>
                            <img
                                src={getAssetQrImageUrl(
                                    buildAssetQrPayload(selectedAssetForQr),
                                )}
                                alt="Asset QR"
                                style={{
                                    width: 220,
                                    height: 220,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                }}
                            />
                        </div>
                    ) : (
                        <p>No asset selected.</p>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setOpenQrModal(false)}
                    >
                        Close
                    </AppSecondaryButton>
                    <AppPrimaryButton
                        type="button"
                        onClick={() =>
                            selectedAssetForQr &&
                            handlePrintLabel(selectedAssetForQr)
                        }
                        disabled={!selectedAssetForQr || !permissions.canPrintLabel}
                    >
                        <Printer size={16} className="me-1" />
                        Print Label
                    </AppPrimaryButton>
                    <AppPrimaryButton
                        type="button"
                        onClick={() => {
                            if (!selectedAssetForQr) return
                            const payload = buildAssetQrPayload(selectedAssetForQr)
                            const qrUrl = getAssetQrImageUrl(payload)
                            const a = document.createElement('a')
                            a.href = qrUrl
                            a.download = `${selectedAssetForQr.AssetTag || selectedAssetForQr.assetId || 'asset'}-qr.png`
                            a.click()
                        }}
                        disabled={!selectedAssetForQr || !permissions.canGenerateQr}
                    >
                        <QrCode size={16} className="me-1" />
                        Download QR
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </>
    )
}

export * from './schedule-inspection'
export * from './types'
export * from './search'
export * from './audit-log'
export * from './template-linking'
export * from './schedule-inspections'
export * from './detail'
export * from './scan'
export * from './locations'
